import PropTypes from "prop-types";
import {
  ArticleOutlined,
  CheckCircleOutlined as CheckCircleIcon,
  MoreVert,
  OpenInFull as MaximizeIcon,
  RemoveCircle,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
} from "@mui/material";
import BookingStatus from "components/BookingStatus.jsx";
import { ListContainer, ListItem, PaymentMatchListItem } from "./lists.jsx";
import {
  borderRadius,
  Button,
  colorsFoundation,
} from "../../../theme/index.js";
import typography from "../../../theme/tokens/typography.js";
import { getDatumZeit, getEuro } from "../../../services/formatService.js";

const PaymentMatchingClaimDetailsPanel = ({
  selectedClaim,
  bookings = [],
  bookingIn = [],
  bookingOut = [],
  differenceAmount,
  matchPayments,
  selectedPayments,
  togglePaymentSelection,
  confirmSelectedPayments,
  loadingMatchings,
  handleOpenClaimDialog,
  menuAnchorEl,
  menuBookingId,
  handleMenuOpen,
  handleMenuClose,
  handleOpenDeleteDialog,
}) => {
  const matches = selectedClaim
    ? matchPayments(selectedClaim)
    : { exact: [], possible: [] };

  if (!selectedClaim) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: borderRadius.md,
          border: `1px solid ${colorsFoundation.neutral[200]}`,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: typography.size.xl }}>
            Schadendetails
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "text.secondary",
          }}
        >
          <ArticleOutlined
            fontSize="large"
            sx={{ mb: 2, color: colorsFoundation.neutral[300] }}
          />
          <Typography variant="body1" color={colorsFoundation.neutral[700]}>
            Bitte wählen Sie einen Schaden aus
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: borderRadius.md,
        border: `1px solid ${colorsFoundation.neutral[200]}`,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontSize: typography.size.xl }}>
            Schadendetails
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: typography.size.md, fontWeight: 600 }}
          >
            {selectedClaim.schaden_nummer_makler}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Vollständige Details anzeigen">
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => handleOpenClaimDialog(selectedClaim)}
            >
              <Typography variant="body2" sx={{ fontSize: typography.size.xs }}>
                Details
              </Typography>
              <IconButton size="small">
                <MaximizeIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            rowGap: 2,
          }}
        >
          {selectedClaim?.schaden_nummer_kd && (
            <InfoBlock
              label="Vorgangsnummer Kunde"
              value={selectedClaim.schaden_nummer_kd}
            />
          )}
          {selectedClaim?.schaden_nummer_vu && (
            <InfoBlock
              label="Schadennummer VR"
              value={selectedClaim.schaden_nummer_vu}
            />
          )}

          <InfoBlock
            label="Objekt"
            value={selectedClaim?.objekt?.bezeichnung}
          />
          <InfoBlock
            label="Datum"
            value={
              selectedClaim.schaden_datum
                ? getDatumZeit(selectedClaim.schaden_datum)
                : "-"
            }
          />
          <InfoBlock
            label="Gesellschaft"
            value={selectedClaim?.vertrag?.gesellschaft?.name}
          />

          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${colorsFoundation.neutral[200]}`,
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 1.25,
            }}
          >
            <InfoAmount
              label="Kostenvoranschlag"
              value={selectedClaim.schaden_kostenvoranschlag}
            />
            <InfoAmount
              label="Rechnungsbetrag"
              value={selectedClaim.schaden_rechnungsbetrag}
            />
            <InfoAmount
              label={selectedClaim.sb_label || "Selbstbeteiligung"}
              value={selectedClaim.sb_betrag}
            />
            <InfoAmount
              label="Auszahlungsbetrag"
              value={selectedClaim.auszahlungsbetrag}
            />
            {bookings && bookings.length > 0 && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: typography.weight.semibold,
                    color: "text.secondary",
                  }}
                >
                  Differenz
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: typography.weight.semibold,
                    textAlign: "right",
                    color: "text.secondary",
                  }}
                >
                  {differenceAmount}
                </Typography>
              </>
            )}
          </Box>

          <Section title="Zahlungseingänge">
            {bookingIn.length > 0 ? (
              <ListContainer>
                {bookingIn.map((booking) => (
                  <ListItem key={booking.id}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {booking.recipient}
                        </Typography>
                        <Typography variant="body2">
                          {booking.description}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking.id)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEl}
                        open={
                          Boolean(menuAnchorEl) && menuBookingId === booking.id
                        }
                        onClose={handleMenuClose}
                      >
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            handleOpenDeleteDialog(booking);
                          }}
                        >
                          <RemoveCircle fontSize="small" sx={{ mr: 1 }} />
                          Zahlungseingang entfernen
                        </MenuItem>
                      </Menu>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Datum:{" "}
                        {getDatumZeit(
                          booking.finished_at || booking.exported_at
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        {getEuro(booking.amount)}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </ListContainer>
            ) : (
              <EmptyHint text="Keine Zahlungseingänge" />
            )}
          </Section>

          {bookingOut.length > 0 && (
            <Section title="Zahlungsausgänge" sx={{ mt: 2 }}>
              <ListContainer>
                {bookingOut.map((booking) => (
                  <ListItem key={booking.id}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {booking.category === "refund" && (
                          <Chip
                            label="Rückzahlung VU"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        <BookingStatus booking={booking} />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {booking.iban}
                      </Typography>
                      <Typography variant="body2">
                        {booking.description}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Datum:{" "}
                        {getDatumZeit(
                          booking.finished_at || booking.exported_at
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            booking.category === "refund"
                              ? "warning.main"
                              : "error.main",
                        }}
                      >
                        -{getEuro(booking.amount)}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </ListContainer>
            </Section>
          )}

          {matches.exact.length > 0 && (
            <Section title="Exakte Zuordnungen" sx={{ mt: 3 }}>
              <ListContainer>
                {matches.exact.map((pm) => (
                  <PaymentMatchListItem
                    key={pm.payment.id}
                    pm={pm}
                    isSelected={selectedPayments.includes(pm.payment.id)}
                    onToggle={togglePaymentSelection}
                  />
                ))}
              </ListContainer>
            </Section>
          )}

          {matches.possible.length > 0 && (
            <Section title="Mögliche Zuordnungen" sx={{ mt: 3 }}>
              <ListContainer>
                {matches.possible.map((pm) => (
                  <PaymentMatchListItem
                    key={pm.payment_id || `possible_${pm.payment?.id}`}
                    pm={pm}
                    isSelected={selectedPayments.includes(pm.payment?.id)}
                    onToggle={togglePaymentSelection}
                  />
                ))}
              </ListContainer>
            </Section>
          )}

          {selectedPayments.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="primary"
                onClick={confirmSelectedPayments}
                disabled={loadingMatchings}
                fullWidth
                sx={{
                  cursor: loadingMatchings ? "not-allowed" : "pointer",
                }}
                startIcon={<CheckCircleIcon />}
              >
                {selectedPayments.length} Zahlung
                {selectedPayments.length !== 1 ? "en" : ""} zuordnen
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Der zuständige Sachbearbeiter wird automatisch über die
                Zuordnung informiert.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const InfoBlock = ({ label, value }) => (
  <Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: typography.weight.semibold,
        color: "text.secondary",
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

const InfoAmount = ({ label, value }) => (
  <>
    <Typography
      variant="body2"
      sx={{
        fontWeight: typography.weight.semibold,
        color: "text.secondary",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: typography.weight.semibold,
        textAlign: "right",
        color: "text.secondary",
      }}
    >
      {value ? getEuro(value) : "-"}
    </Typography>
  </>
);

const Section = ({ title, children, sx: customSx }) => (
  <Box sx={{ mt: 1, ...customSx }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const EmptyHint = ({ text }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ fontStyle: "italic" }}
  >
    {text}
  </Typography>
);

PaymentMatchingClaimDetailsPanel.propTypes = {
  selectedClaim: PropTypes.object,
  bookings: PropTypes.array,
  bookingIn: PropTypes.array,
  bookingOut: PropTypes.array,
  differenceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matchPayments: PropTypes.func.isRequired,
  selectedPayments: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ).isRequired,
  togglePaymentSelection: PropTypes.func.isRequired,
  confirmSelectedPayments: PropTypes.func.isRequired,
  loadingMatchings: PropTypes.bool.isRequired,
  handleOpenClaimDialog: PropTypes.func.isRequired,
  menuAnchorEl: PropTypes.object,
  menuBookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleMenuOpen: PropTypes.func.isRequired,
  handleMenuClose: PropTypes.func.isRequired,
  handleOpenDeleteDialog: PropTypes.func.isRequired,
};

InfoBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

InfoAmount.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

EmptyHint.propTypes = {
  text: PropTypes.string.isRequired,
};

export default PaymentMatchingClaimDetailsPanel;
